import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { RouterModule } from "@angular/router";
import { ComplianceService } from "../../services/compliance.service";
import { UserManagementService } from "../../user-management/user-management.service";
import { PRIVACY_VERSION, TERMS_VERSION } from "../legal-versions";

/**
 * GDPR / ePrivacy cookie-consent banner. Shown until the user records a choice
 * for the current PRIVACY_VERSION. The choice is stored locally (so the banner
 * works for unauthenticated visitors) and mirrored to the backend
 * (POST me/consents) whenever a session exists.
 */
@Component({
  selector: "app-cookie-consent",
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <div
      class="cookie-consent"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      *ngIf="visible"
    >
      <div class="cookie-consent__text">
        <strong>We value your privacy</strong>
        <p>
          We use strictly necessary cookies to keep you signed in. With your
          consent we also use optional analytics cookies to improve Purifai. You
          can change your choice any time under <em>My Data &amp; Privacy</em>.
          See our
          <a routerLink="/privacy">Privacy Policy</a>.
        </p>
      </div>
      <div class="cookie-consent__actions">
        <button type="button" class="btn-link" (click)="reject()">
          Necessary only
        </button>
        <button type="button" class="btn-accept" (click)="accept()">
          Accept all
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      .cookie-consent {
        position: fixed;
        left: 16px;
        right: 16px;
        bottom: 16px;
        z-index: 6000;
        max-width: 760px;
        margin: 0 auto;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 16px;
        padding: 16px 20px;
        background: #ffffff;
        color: #1f2937;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
      }
      .cookie-consent__text {
        flex: 1 1 320px;
        font-size: 13px;
        line-height: 1.45;
      }
      .cookie-consent__text p {
        margin: 4px 0 0;
      }
      .cookie-consent__text a {
        color: #2563eb;
        text-decoration: underline;
      }
      .cookie-consent__actions {
        display: flex;
        gap: 10px;
        align-items: center;
      }
      .btn-link {
        background: transparent;
        border: 1px solid #cbd5e1;
        color: #334155;
        padding: 8px 14px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
      }
      .btn-accept {
        background: #2563eb;
        border: 1px solid #2563eb;
        color: #fff;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 13px;
      }
      .btn-accept:hover {
        background: #1d4ed8;
      }
    `,
  ],
})
export class CookieConsentComponent implements OnInit {
  private static readonly STORAGE_KEY = "cookie_consent";
  visible = false;

  constructor(
    private complianceService: ComplianceService,
    private userService: UserManagementService
  ) {}

  ngOnInit(): void {
    this.visible = !this.hasRecordedCurrentConsent();
  }

  accept(): void {
    this.record(true);
  }

  reject(): void {
    this.record(false);
  }

  private hasRecordedCurrentConsent(): boolean {
    try {
      const raw = localStorage.getItem(CookieConsentComponent.STORAGE_KEY);
      if (!raw) {
        return false;
      }
      const saved = JSON.parse(raw);
      // Re-prompt if the policy version changed since the last decision.
      return saved?.privacy_version === PRIVACY_VERSION;
    } catch {
      return false;
    }
  }

  private record(analytics: boolean): void {
    const consent = {
      cookies: true, // strictly necessary cookies are always on
      analytics,
      terms_version: TERMS_VERSION,
      privacy_version: PRIVACY_VERSION,
      accepted_at: new Date().toISOString(),
    };

    try {
      localStorage.setItem(
        CookieConsentComponent.STORAGE_KEY,
        JSON.stringify(consent)
      );
    } catch {
      // Ignore storage errors; the banner will simply re-appear next load.
    }

    this.visible = false;

    // Mirror to the backend audit trail when the visitor is authenticated.
    if (this.userService.hasValidAuthSession()) {
      this.complianceService
        .saveConsents({
          cookies: consent.cookies,
          analytics: consent.analytics,
          terms_version: TERMS_VERSION,
          privacy_version: PRIVACY_VERSION,
        })
        .subscribe({
          next: () => {},
          error: () => {},
        });
    }
  }
}
